import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { openModal } from '../../../store/slices/modal';

import useGetJobPostList from '../../../hooks/job_posts/useGetJobPosts';
import useArchiveJobPosts from '../../../hooks/job_posts/useArchiveJobPosts';
import useCopyJobPost from '../../../hooks/job_posts/useCopyJobPost';

import JobPostsListTable from '../tables/JobPostsList';
import { SVGIcon } from '../../wrappers/SVGIcon';

import { JobPostStatus, IJobPostList, JobPortalTab } from '../../../interfaces/job_posts';

import { Icons } from '../../../data/Assets';
import CheckBox from '../../reusables/CheckBox';

interface IJobPostsProps {
    jobPostStatus: JobPostStatus;
    setCurrentTab: (tab: JobPortalTab) => void;
}

const JobPosts = (props: IJobPostsProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { jobPostStatus, setCurrentTab } = props;

    const [selectedJobPosts, setSelectedJobPosts] = useState<number[]>([]);
    const [jobPosts, setJobPosts] = useState<IJobPostList[]>([]);
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const { data: jobPostsData, isLoading: isJobPostsLoading } = useGetJobPostList(jobPostStatus);

    const { mutate: archiveJobPosts } = useArchiveJobPosts();
    const { mutate: copyJobPost } = useCopyJobPost();

    useEffect(() => {
        setJobPosts(jobPostsData || []);
    }, [jobPostsData]);

    const handleToggleSelectJobPost = (jobPostId: number) => {
        const isJobPostSelected = selectedJobPosts.includes(jobPostId);
        if (isJobPostSelected) {
            setSelectedJobPosts(selectedJobPosts.filter((id) => id !== jobPostId));
        } else {
            setSelectedJobPosts([...selectedJobPosts, jobPostId]);
        }
    }

    const handleToggleSelectAllJobPosts = () => {
        const isAllJobPostsSelected = selectedJobPosts.length === jobPosts.length;
        if (isAllJobPostsSelected) {
            setSelectedJobPosts([]);
        } else {
            setSelectedJobPosts(jobPosts.map((job) => job.id));
        }
    }

    const handleConfirmDelete = (content: string[], callback: () => void) => {
        if (selectedJobPosts.length === 0) {
            return;
        }

        dispatch(openModal({
            name: 'MESSAGE_MODAL',
            data: {
                view: 'ARCHIVE_JOBS',
                content: content,
                action: callback,
                cancelFn: callback,
                cancelTxt: 'Cancel',
            }
        }));
    }

    const handleDeleteSelectedJobPosts = () => {
        const jobsToArchive = jobPosts.filter((jobPost) => selectedJobPosts.includes(jobPost.id)).map((jobPost) => `${jobPost.job_title}, ${jobPost.company}`);
        const modalContent = jobsToArchive.map((jobPost) => `${jobPost} will be archived.`);

        handleConfirmDelete(modalContent, () => {
            archiveJobPosts({
                payload: {
                    ids: selectedJobPosts,
                },
                setIsProcessing,
                setErrorMessage,
            });

            setJobPosts(jobPosts.filter((jobPost) => !selectedJobPosts.includes(jobPost.id)));
            setSelectedJobPosts([]);
        });
    }

    const onSelectButtonClick = (checked: boolean) => {
        if (isLoading || jobPosts.length === 0) {
            return;
        }

        setSelectAll(checked);
        handleToggleSelectAllJobPosts();
    }

    const handleEditJobPost = (jobPostId: number) => {
        navigate(`/job-portal/${jobPostId}/details`);
    }

    const handleArchiveJobPost = (jobPostId: number) => {
        const jobPost = jobPosts.find((jobPost) => jobPost.id === jobPostId);

        const modalContent = [
            `${jobPost?.job_title}, ${jobPost?.company} will be archived.`,
        ]

        dispatch(openModal({
            name: 'MESSAGE_MODAL',
            data: {
                view: 'ARCHIVE_JOBS',
                content: modalContent,
                action: () => {
                    archiveJobPosts({
                        payload: {
                            ids: [jobPostId],
                        },
                        setIsProcessing,
                        setErrorMessage,
                    });

                    setJobPosts(jobPosts.filter((jobPost) => jobPost.id !== jobPostId));
                },
                cancelFn: () => {
                    setSelectedJobPosts([]);
                },
                cancelTxt: 'Cancel',
            }
        }));
    }

    const handleCopyJobPost = (jobPostId: number) => {
        copyJobPost({
            payload: {
                id: jobPostId,
            },
            setIsProcessing,
            setErrorMessage,
            successCallback: (data: IJobPostList) => {
                setJobPosts([data, ...jobPosts]);
                setCurrentTab(JobPortalTab.DRAFTS);
            },
        });
    }

    useEffect(() => {
        if (jobPosts.length === 0) {
            setSelectAll(false);
            return;
        }

        if (selectedJobPosts.length !== jobPosts.length) {
            setSelectAll(false);
        } else {
            setSelectAll(true);
        }
    }, [selectedJobPosts, jobPosts]);

    const isLoading = isJobPostsLoading || isProcessing;

    return (
        <div className="immployer__lists__page_view">
            <br />
            {jobPostStatus !== JobPostStatus.ARCHIVED && (
                <div className="immployer__lists__top_section_left">
                    {/* <SearchInput /> */}
                    <CheckBox checked={selectAll} disabled={isLoading || jobPosts.length === 0} onClick={(e, checked) => onSelectButtonClick(checked)} />
                    <button
                        disabled={isLoading || selectedJobPosts.length === 0}
                        onClick={handleDeleteSelectedJobPosts}
                    >
                        <SVGIcon src={Icons.Trash3} color={'grey'} hoverColor={selectedJobPosts.length === 0 ? undefined : 'dark_grey'} />
                    </button>
                </div>
            )}
            <JobPostsListTable
                isLoading={isJobPostsLoading}
                jobPosts={jobPosts}
                selectedJobPosts={selectedJobPosts}
                disableActions={jobPostStatus === JobPostStatus.ARCHIVED}
                onSelectJobPost={handleToggleSelectJobPost}
                onEditJobPost={handleEditJobPost}
                onCopyJobPost={handleCopyJobPost}
                onArchiveJobPost={handleArchiveJobPost}
            />
        </div>
    )
}

export default JobPosts;